import * as benefitsLogos from '../images/membership-benefits';
import * as logos from '../images/subscription-logos';
import * as productsLogos from '../images/products';
import moment from 'moment';
import React from 'react';
import { Benefit } from '../components/NewDashboard/Benefit';
import { Certifications } from '../components/NewDashboard/Certifications';
import { ClientScores } from '../components/NewDashboard/ClientScores';
import { ProductCard } from '../components/NewDashboard/ProductCard';
import { Questionnaire } from '../components/NewDashboard/Questionnaire';
import { QuickLinks } from '../components/NewDashboard/QuickLinks';
import { SubscriptionTypePublicId } from '@pec/aion-ui-core/enums/Subscription';
import { ToDoList } from '../components/NewDashboard/ToDoList';
import { Verifications } from '../components/NewDashboard/Verifications';
import { VerificationsQuickLinks } from '../components/NewDashboard/VerificationsQuickLinks';
import { Connections } from '../components/NewDashboard/Connections';

export enum Component {
  Benefit = 'Benefit',
  Certifications = 'Certifications',
  ClientScores = 'ClientScores',
  ProductCard = 'ProductCard',
  Questionnaire = 'Questionnaire',
  QuickLinks = 'QuickLinks',
  ToDoList = 'ToDoList',
  Verifications = 'Verifications',
  VerificationsQuickLinks = 'VerificationsQuickLinks',
  Connections = 'Connections'
}

export enum FoundationProducts {
  ChasRams = 'CHAS RAMS',
  OnlineTraining = 'Online Training',
  CreditSafe = 'Creditsafe',
  TradePoint = 'Trade Point',
  FuelCardServices = 'Fuel Card Services',
  VehicleComplianceScheme = 'Vehicle Compliance Scheme',
  ChasSocialSustainability = 'CHAS Social Sustainability',
  Xero = 'Xero',
  Lighthouse = 'Lighthouse',
  LoveJunk = 'LoveJunk',
  AnyJunk = 'AnyJunk',
  ChasFuelCard = 'CHAS Fuel Card',
  FuelStore = 'The Fuel Store',
  AutoWindscreens = 'Auto Windscreens',
  GoWash = 'GoWash',
  HealthKey = 'HealthKey'
}

export const componentMapper = {
  [Component.Benefit]: Benefit,
  [Component.Certifications]: Certifications,
  [Component.ClientScores]: ClientScores,
  [Component.ProductCard]: ProductCard,
  [Component.Questionnaire]: Questionnaire,
  [Component.QuickLinks]: QuickLinks,
  [Component.ToDoList]: ToDoList,
  [Component.Verifications]: Verifications,
  [Component.VerificationsQuickLinks]: VerificationsQuickLinks,
  [Component.Connections]: Connections
};

export const LOGOS: Record<string, React.FC<React.SVGProps<SVGSVGElement>>> = {
  US: logos.VeriforceLogo,
  [SubscriptionTypePublicId.ChasVerifiedContractorAU]: logos.CHASVerifiedContractorLogo,
  [SubscriptionTypePublicId.ChasVerifiedSupplierAU]: logos.CHASVerifiedSupplierLogo,
  [SubscriptionTypePublicId.ChasEliteAU]: logos.CHASEliteLogo,
  [SubscriptionTypePublicId.ChasAdvanced]: logos.CHASAdvancedLogo,
  [SubscriptionTypePublicId.ChasVCS]: logos.CHASVCSLogo,
  [SubscriptionTypePublicId.ChasElite]: logos.CHASEliteLogo,
  [SubscriptionTypePublicId.ChasVerifiedSupplierGB]: logos.CHASVerifiedSupplierLogo,
  [SubscriptionTypePublicId.ChasFoundation]: logos.CHASFoundationLogo,
  [SubscriptionTypePublicId.ChasStandard]: logos.CHASStandardLogo,
  [SubscriptionTypePublicId.ChasSocialSustainability]: logos.CHASSocialSustainabilityLogo
};

export const BENEFITS_LOGOS: Record<string, React.FC<React.SVGProps<SVGSVGElement>>> = {
  fleetCheck: benefitsLogos.FleetCheckLogo,
  tradePoint: benefitsLogos.TradePointLogo,
  fuelCardServices: benefitsLogos.FuelCardServicesLogo,
  normCyber: benefitsLogos.NormCyberLogo,
  speedyHire: benefitsLogos.SpeedyHireLogo,
  safetyCulture: benefitsLogos.SafetyCultureLogo,
  lighthouse: benefitsLogos.LighthouseLogo,
  vanaways: benefitsLogos.VanawaysLogo,
  xero: benefitsLogos.XeroLogo,
  chasAccidentHotline: benefitsLogos.ChasAccidentHotlineLogo,
  anyJunk: benefitsLogos.AnyJunkLogo,
  loveJunk: benefitsLogos.LoveJunkLogo,
  chasFuelCard: benefitsLogos.ChasFuelCardLogo,
  chasAutoWindscreens: benefitsLogos.ChasWindScreensLogo,
  healthkey: benefitsLogos.HealthKeyLogo,
  chasHalfordsLogo: benefitsLogos.ChasHalfordsLogo,
  chasRAMS: benefitsLogos.handsHQ,
  chasProtect: benefitsLogos.ChasProtect,
  chasBusinessShieldLogo: benefitsLogos.ChasBusinessShieldLogo,
  chasLegalHelpLine: benefitsLogos.ChasLegalHelplineLogo,
  humanFocusLogo: benefitsLogos.ChasHumanFocusLogo,
  theaa: benefitsLogos.theaa,
  westWallasey: benefitsLogos.westWallasey
};

export const PRODUCTS_LOGOS: Record<string, React.FC<React.SVGProps<SVGSVGElement>>> = {
  chasRams: productsLogos.ChasRamsLogo,
  onlineTraining: productsLogos.OnlineTrainingLogo,
  chasProtect: productsLogos.ChasProtectLogo,
  businessShield: productsLogos.BusinessShieldLogo,
  helpline: productsLogos.HelplineLogo,
  socialSustainability: productsLogos.SocialSustainabilityLogo,
  chasVCS: productsLogos.ChasVCSLogo
};

export const FOUNDATION_EXPIRATION_DATE = '3000-01-01';

/**
 * Returns true when the subscription has a real expiration date, i.e. one before the
 * `FOUNDATION_EXPIRATION_DATE` sentinel used to represent "no real expiry".
 */
export const hasRealExpiration = (expirationDate?: string | null): boolean =>
  !!expirationDate && moment(expirationDate).isBefore(FOUNDATION_EXPIRATION_DATE);

export const foundationProducts: FoundationProducts[] = [
  FoundationProducts.ChasRams,
  FoundationProducts.OnlineTraining,
  FoundationProducts.CreditSafe,
  FoundationProducts.TradePoint,
  FoundationProducts.FuelCardServices,
  FoundationProducts.VehicleComplianceScheme,
  FoundationProducts.ChasSocialSustainability,
  FoundationProducts.Xero,
  FoundationProducts.Lighthouse,
  FoundationProducts.AnyJunk,
  FoundationProducts.LoveJunk,
  FoundationProducts.ChasFuelCard,
  FoundationProducts.FuelStore,
  FoundationProducts.AutoWindscreens,
  FoundationProducts.GoWash,
  FoundationProducts.HealthKey
];
